import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {PAContext} from "../../../Services/PAContext";
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    Tooltip, List, IconButton,
    ListItemButton, ListItemIcon, Paper, Avatar, TextField
} from "@mui/material";
import Grid from '@mui/material/Grid';
import ListItemText from "@mui/material/ListItemText";
import dayjs from "dayjs";
import FlightCard from "../Flights/FlightCard";
import Typography from "@mui/material/Typography";
import './flights.css';
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import {capitalizeFirstLetter, formatName, stringAvatar, stringToColor} from "../../Helpers/TextStyling";
import Box from "@mui/material/Box";
import {Stack} from "@mui/system";
import {getAllContactsFromDatabase} from "../../Restricted/Contacts/BrowserDatabaseFunctions";





const calculateTotalTime = (flightsForDay) => {
    const firstFlight = flightsForDay[0]; // First flight of the day
    const lastFlight = flightsForDay[flightsForDay.length - 1]; // Last flight of the day

    const startTime = dayjs(firstFlight.depart_timestamp);
    const endTime = dayjs(lastFlight.arrive_timestamp);

    // Total time in minutes between the first departure and the last arrival
    const totalMinutes = endTime.diff(startTime, 'minute');

    return totalMinutes;
};



function FlightsList(props) {

    const navigate = useNavigate();

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    // Load Contacts from IndexedDB when authenticated
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;

        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) {
                    setDbContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);

    const [ attendeesReferenceArray, setAttendeesReferenceArray ] = useState( authenticated
        ? dbContacts
        : SampleAttendees);

    useEffect(() => {

        console.log("authenticated, dbContacts, SampleAttendees triggered ...");

        setAttendeesReferenceArray(
            authenticated
                ? dbContacts
                : SampleAttendees
        );
    }, [authenticated, dbContacts, SampleAttendees]);


    const [eventFlights, setEventFlights] = useState();

    const [selectedAttendee, setSelectedAttendee] = useState(() => {
        // find the attendee for the current account
        const currentAttendee = PA_GlobalObject.Planning.attendees.find(att => att.id === Account.id);

        if (currentAttendee && currentAttendee.role !== "planner") {
            return Account.id; // use current account if not a planner
        }

        // otherwise, pick someone else who isn't a planner
        const otherAttendee = attendeesReferenceArray.find(att => att.role !== "planner");

        // if found, return their id, else default to ''
        return otherAttendee ? otherAttendee.id : '';
    });

    // 1. Memoize the attendee list and counts
    const sortedAttendees = useMemo(() => {
        const counts = PA_GlobalObject.Planning.attendees
            .filter(att => att.role !== 'planner')
            .map(att => {
                const count = PA_GlobalObject.Planning.flights.reduce((acc, f) => {
                    const isMatch = f.attendee?.id === att.id;
                    const isNotCancelled = f.status !== 'Cancelled';

                    return acc + (isMatch && isNotCancelled ? 1 : 0);
                }, 0);
                return { ...att, flightCount: count };
            });

        const withFlights = counts.filter(a => a.flightCount > 0).sort((a, b) => b.flightCount - a.flightCount);
        const withoutFlights = counts.filter(a => a.flightCount === 0).sort((a, b) => a.nickname.localeCompare(b.nickname));

        return [...withFlights, ...withoutFlights];
    }, [PA_GlobalObject.Planning.flights, attendeesReferenceArray]);

    // 2. Memoize the actual flight filtering
    const filteredFlights = useMemo(() => {
        return PA_GlobalObject.Planning.flights.filter(f =>
            f.attendee && String(f.attendee.id) === String(selectedAttendee)
        );
    }, [PA_GlobalObject.Planning.flights, selectedAttendee]);

    // 3. Memoize the grouping by day
    const flightsByDay = useMemo(() => {
        return filteredFlights.reduce((groups, flight) => {
            const date = dayjs(flight.depart_timestamp).format('YYYY-MM-DD');
            if (!groups[date]) groups[date] = [];
            groups[date].push(flight);
            return groups;
        }, {});
    }, [filteredFlights]);



    const handleChipClick = (_attendee_id) => {
        console.log("Show flights for : "+_attendee_id);
        setSelectedAttendee(_attendee_id);
    }

    const [anchorFlightsListMenu, setAnchorFlightsListMenu] = useState(null);
    const handleFlightsListMenuOpenMenu = (event) => {
        setAnchorFlightsListMenu(event.currentTarget);
    };
    const handleFlightsListMenuClick = (event) => {
        const target = event.currentTarget;
        const dataId = target.getAttribute('data-id');

        handleFlightsListMenuClose();

        if (dataId === "searchAndBook") {
            navigate('/Planner/Flights/SearchAndBook');
        } else if (dataId === "search"){
            navigate('/Planner/Flights/Search');
        } else if (dataId === "enterDetails"){
            navigate('/Planner/Flights/EnterDetails');
        }
    };
    const handleFlightsListMenuClose = () => {
        setAnchorFlightsListMenu(null);
    };



    const [flightToDelete, setFlightToDelete] = useState(null);  // Holds the flight to delete for confirmation dialog

    const coTravelerCount = useMemo(() => {
        if (!flightToDelete) return 0;

        console.log("coTravelerCount : ", flightToDelete );

        return filteredFlights.filter(f =>
            f.flight_number === flightToDelete.flight_number &&
            f.depart_timestamp === flightToDelete.depart_timestamp &&
            f.id !== flightToDelete.id // Don't count yourself
        ).length;
    }, [flightToDelete, eventFlights]);

    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);  // Controls the confirmation dialog visibility
    // 1. The actual "Red Button" execution
    const handleDeleteFlight = (flightId) => {
        // Filter local state
        const updatedFlights = eventFlights.filter(f => f.id !== flightId);
        setEventFlights(updatedFlights);

        // Update Global State (Using the functional updater to avoid race conditions)
        set_PA_GlobalObject((prev) => ({
            ...prev, // ALWAYS spread the prev state so you don't lose other top-level keys!
            Planning: {
                ...prev.Planning,
                flights: updatedFlights,
            },
        }));

        // Socket Communication
        if (PA_GlobalObject.Planning?.status !== 10) {
            sendSocketMessage({
                action: "E_deleteEventFlight_v2",
                event_id: PA_GlobalObject.Planning.id,
                flight_id: flightId
            });
        }

        closeDeleteDialog();
    };

    // 2. The "Open the Safety Gate" function
    const openDeleteDialog = (flight) => {
        console.log("openDeleteDialog : ", flight );
        setFlightToDelete(flight);
        setDeleteDialogOpen(true);
    };

    // 3. The "Close the Safety Gate" function
    const closeDeleteDialog = () => {
        setFlightToDelete(null);
        setDeleteDialogOpen(false);
    };


    const [isCancelledDetailOpen, setCancelledDetailOpen] = useState(false);
    const [selectedCancelledFlight, setSelectedCancelledFlight] = useState(null);

    // Status Options Configuration
    const STATUS_OPTIONS = [
        { label: 'Scheduled', code: 'S', color: '#757575', icon: 'icon-clock' },
        { label: 'On Time', code: 'On Time', color: '#2e7d32', icon: 'icon-check' },
        { label: 'Delayed', code: 'Delayed', color: '#d32f2f', icon: 'icon-warning' },
        { label: 'Landed', code: 'L', color: '#1976d2', icon: 'icon-airplane' },
        { label: 'Cancelled', code: 'C', color: '#e74c3c', icon: 'icon-cross' },
        { label: 'Missed', code: 'Missed', color: '#94a3b8', icon: 'icon-no' }, // 👈 Added Missed Option
        { label: 'Diverted', code: 'D', color: '#ef6c00', icon: 'icon-redo' },
    ];

    const [isStatusPickerOpen, setStatusPickerOpen] = useState(false);
    const [flightToUpdate, setFlightToUpdate] = useState(null);

    const openStatusPicker = (flight) => {
        setFlightToUpdate(flight);
        setStatusPickerOpen(true);
    };


    // Manual Status Update Logic inside FlightsList
    const handleManualStatusUpdate = (flight, newStatus) => {
        if (!flight) return;

        console.log("updated flight : ", flight);

        const statusTimestamp = new Date().toISOString();

        // 1. Update Global Store (Zustand)
        set_PA_GlobalObject((prev) => {
            const updatedFlights = prev.Planning.flights.map((f) =>
                f.id === flight.id
                    ? { ...f, status: newStatus, status_timestamp: statusTimestamp }
                    : f
            );

            return {
                ...prev,
                Planning: {
                    ...prev.Planning,
                    flights: updatedFlights,
                },
            };
        });

        // 2. Socket Communication
        if (PA_GlobalObject.Planning?.status !== 10) {

            const departDate = dayjs(flight.depart_timestamp);
            const statusTimestamp = new Date().toISOString();

            const _sendString = {
                action: "E_updateEventFlight_v2",
                event_id: PA_GlobalObject.Planning.id,
                // Wrap details in a flight object to match server expectations
                flight: {
                    flight_id: flight.id,
                    airline_id: flight.airline_code, // e.g., "AA"
                    flight_number: flight.flight_number,
                    depart_airport_code: flight.depart_airport_code,
                    arrive_airport_code: flight.arrive_airport_code,
                    depart_date_year: departDate.year(),
                    depart_date_month: departDate.month() + 1, // JS months are 0-indexed
                    depart_date_day: departDate.date(),
                    amount_total: flight.amount_total,
                    currency: flight.currency,
                    is_business_expense: flight.attendee.is_business_expense,
                    business_expense_amount: flight.attendee.business_expense_amount,
                    attendee_id: flight.attendee.id, // Crucial for permission check
                    alerts_enabled : flight.alerts_enabled,
                    status: newStatus,
                    status_timestamp: statusTimestamp,
                }
            };

            sendSocketMessage(_sendString);
        }

        setStatusPickerOpen(false);

    };

    // Status Color Helper
    const getStatusColor = (status) => {
        const option = STATUS_OPTIONS.find(o => o.label.toLowerCase() === status.toLowerCase() || o.code === status);
        return option ? option.color : '#757575';
    };



    return (
        <>
            <div className="flights-list-container">

                {/* --- FLIGHTS HEADER --- */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        boxShadow: '0 4px 24px -6px rgba(0,0,0,0.06)',
                        borderRadius: '12px 12px 0 0',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                        {/* Title Area */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', // Flight Blue gradient
                                p: 0.9,
                                borderRadius: '10px',
                                display: 'flex',
                                boxShadow: '0 4px 10px rgba(14, 165, 233, 0.3)'
                            }}>
                                <i className="icon-airplane" style={{ color: 'white', fontSize: '18px' }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', letterSpacing: -0.4, whiteSpace: 'nowrap' }}>
                                    Flights
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <i className="icon-earth" style={{ color: '#0ea5e9', fontSize: '10px' }} />
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.1, whiteSpace: 'nowrap' }}>
                                        Travel Itinerary & Status
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Controls Area */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

                            {/* Flights Menu Add Button */}
                            <Tooltip title="Add Flight">
                                <IconButton
                                    onClick={handleFlightsListMenuOpenMenu}
                                    sx={{
                                        ml: 0.5,
                                        bgcolor: '#1e293b',
                                        color: 'white',
                                        borderRadius: '12px',
                                        width: '38px',
                                        height: '38px',
                                        boxShadow: '0 4px 12px rgba(30, 41, 59, 0.25)',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: '#334155',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 6px 16px rgba(30, 41, 59, 0.3)',
                                        },
                                        '&:active': { transform: 'translateY(0px)' }
                                    }}
                                >
                                    <i className="icon-plus" style={{ fontSize: '20px' }} />
                                </IconButton>
                            </Tooltip>

                            {/* Flights Action Menu */}
                            <Menu
                                anchorEl={anchorFlightsListMenu}
                                open={Boolean(anchorFlightsListMenu)}
                                onClose={handleFlightsListMenuClose}
                                PaperProps={{
                                    sx: {
                                        borderRadius: '12px',
                                        mt: 1,
                                        minWidth: 220,
                                        boxShadow: '0px 10px 25px rgba(0,0,0,0.2)',
                                    }
                                }}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            >
                                {Account.role === "master_admin" && (
                                    <MenuItem
                                        onClick={handleFlightsListMenuClick}
                                        data-id="searchAndBook"
                                        sx={{ gap: 1.5, py: 1.2 }}
                                    >
                                        <i className="icon-ticket" style={{ color: '#6366f1' }} />
                                        <Typography variant="body2" fontWeight={500}>Search & Book</Typography>
                                    </MenuItem>
                                )}

                                <MenuItem
                                    onClick={handleFlightsListMenuClick}
                                    data-id="search"
                                    sx={{ gap: 1.5, py: 1.2 }}
                                >
                                    <i className="icon-search" style={{ color: '#0ea5e9' }} />
                                    <Typography variant="body2" fontWeight={500}>Add By Search</Typography>
                                </MenuItem>

                                <Divider sx={{ my: 0.5, opacity: 0.6 }} />

                                <MenuItem
                                    onClick={handleFlightsListMenuClick}
                                    data-id="enterDetails"
                                    sx={{ gap: 1.5, py: 1.2 }}
                                >
                                    <i className="icon-pencil" style={{ color: '#64748b' }} />
                                    <Typography variant="body2" fontWeight={500}>Enter Details Manually</Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                </Paper>

                {/* Attendee List */}
                <div className="flights-list-attendee-list-container"
                     style={{
                        display: 'flex',
                        overflowX: 'auto',
                        msOverflowStyle: 'none',
                        alignItems: 'flex-start'
                    }}
                >
                    {sortedAttendees.map((attendee) => {
                        // 1. Logic to find the real avatar data
                        const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === attendee.id);
                        const isSelected = selectedAttendee === attendee.id;

                        const fullName = attendee.nickname?.trim()
                            ? `${formatName(attendee.nickname)}`
                            : `${formatName(attendee.fname)} ${formatName(attendee.lname)}`;

                        return (
                            <Box
                                key={attendee.id}
                                onClick={() => handleChipClick(attendee.id)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    minWidth: '60px',
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                {/* 2. The Notification Badge for Flight Counts */}
                                <Badge
                                    badgeContent={attendee.flightCount > 0 ? attendee.flightCount : null}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: '#E0F2F1', // Soft Mint/Teal
                                            color: '#00695C',           // Dark Teal text
                                            fontSize: '10px',
                                            fontWeight: 900,
                                            border: '2px solid #fff',
                                            top: 5,
                                            right: 5
                                        },
                                    }}
                                >
                                    {/* 3. The Avatar */}
                                    <Box sx={{
                                        p: '3px',
                                        borderRadius: '50%',
                                        border: isSelected ? '2px solid #151269' : '2px solid transparent',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        {
                                            !attendeeAvatar?.avatar || !attendeeAvatar?.avatar.preview ? (
                                                <Avatar
                                                    {...stringAvatar(fullName)}
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        fontSize: '16px',
                                                        fontWeight: 600,
                                                        boxShadow: isSelected ? '0 4px 10px rgba(21, 18, 105, 0.2)' : 'none',
                                                        border: `1px solid ${stringToColor(fullName)}`
                                                    }}
                                                />
                                            ) : (
                                                <Avatar
                                                    src={attendeeAvatar.avatar.preview}
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        boxShadow: isSelected ? '0 4px 10px rgba(21, 18, 105, 0.2)' : 'none',
                                                        border: `1px solid ${stringToColor(fullName)}`
                                                    }}
                                                />
                                            )
                                        }
                                    </Box>
                                </Badge>

                                {/* 4. Name Label */}
                                <Typography sx={{
                                    fontSize: '11px',
                                    fontWeight: isSelected ? 800 : 500,
                                    mt: 1,
                                    color: isSelected ? '#151269' : '#666',
                                    textAlign: 'center',
                                    maxWidth: '70px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {capitalizeFirstLetter(attendee.nickname || attendee.fname)}
                                </Typography>
                            </Box>
                        );
                    })}
                </div>


                {/* show the list of flights */}
                <div
                    className={
                        props.showingPageHeader
                            ? "flights-list-items-container with-header"
                            : "flights-list-items-container"
                    }
                >

                    {/* Check if there are no flights */}
                    {
                        filteredFlights.length === 0 ? (
                            <Typography variant="body2" sx={{
                                textAlign: 'center',
                                mt: 2,  // Adds space on the top (use 2, 3, etc. for different spacing)
                                mb: 2,  // Adds space on the bottom
                            }}>
                                No flights
                                for {PA_GlobalObject.Planning.attendees.find((_att) => _att.id === selectedAttendee?.id)?.nickname || 'this Attendee'}
                            </Typography>
                        ) : (
                            Object.keys(flightsByDay)
                                .filter((_flight) => _flight.status !== "Cancelled")
                                .sort((a, b) => dayjs(a).diff(dayjs(b)))
                                .map((day) => {
                                    const flightsForDay = flightsByDay[day]
                                        .sort((a, b) => dayjs(a.depart_timestamp).diff(dayjs(b.depart_timestamp)))  // Sort flights by time;

                                    // Calculate total time spent from first departure to last arrival for this day
                                    const totalMinutes = calculateTotalTime(flightsForDay);
                                    const totalHours = Math.floor(totalMinutes / 60);
                                    const remainingMinutes = totalMinutes % 60;

                                    return (
                                        <Card
                                            key={day}
                                            sx={{
                                                borderRadius: '5px',
                                                border: '1px solid #f09637',
                                                mb: 3
                                            }}
                                        >
                                            <CardContent>
                                                {/* Display the day and date */}
                                                <Typography variant="h6" color="primary">
                                                    {dayjs(day).format('dddd, MMM D')} {/* Example: "Wednesday, Jun 5" */}
                                                </Typography>
                                                {/* Display total time in air and airports */}
                                                <Typography variant="body2" color="textSecondary"
                                                            sx={{mb: 2, textAlign: 'right'}}>
                                                    Total: {totalHours} h {remainingMinutes} m
                                                </Typography>

                                                {/* Render flights for that day, sorted by depart_timestamp */}
                                                {flightsForDay
                                                    .sort((a, b) => dayjs(a.depart_timestamp).diff(dayjs(b.depart_timestamp)))
                                                    .map((flightDetails, index) => {
                                                        const nextFlight = flightsForDay[index + 1];
                                                        let layoverTime = null;
                                                        const isCancelled = flightDetails.status === "Cancelled"; // 👈 Catch the status

                                                        if (nextFlight) {
                                                            const arrivalTime = dayjs(flightDetails.arrive_timestamp);
                                                            const nextDepartureTime = dayjs(nextFlight.depart_timestamp);
                                                            layoverTime = nextDepartureTime.diff(arrivalTime, 'minute');
                                                        }

                                                        const layoverHours = Math.floor(layoverTime / 60);
                                                        const layoverMinutes = layoverTime % 60;

                                                        return (
                                                            <Grid key={flightDetails.id} item sx={{ display: 'flex', flexDirection: 'column' }}>

                                                                {isCancelled ? (
                                                                    /* 1. THE CANCELLED STRIP (Minimalist view) */
                                                                    <Box
                                                                        onClick={() => {
                                                                            setSelectedCancelledFlight(flightDetails);
                                                                            setCancelledDetailOpen(true);
                                                                        }}
                                                                        sx={{
                                                                            p: 1.5,
                                                                            mb: 1,
                                                                            borderRadius: '12px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            bgcolor: 'rgba(231, 76, 60, 0.05)', // Soft red tint
                                                                            border: '1px dashed #e74c3c',
                                                                            cursor: 'pointer',
                                                                            transition: '0.2s',
                                                                            '&:hover': { bgcolor: 'rgba(231, 76, 60, 0.1)' }
                                                                        }}
                                                                    >
                                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                                            <Box sx={{ bgcolor: '#e74c3c', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                <i className="icon-cross" style={{ color: '#fff', fontSize: '10px' }} />
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography sx={{ fontSize: '10px', fontWeight: 900, color: '#e74c3c', letterSpacing: 1 }}>CANCELLED</Typography>
                                                                                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#2c3e50' }}>
                                                                                    {flightDetails.flight_number} • {flightDetails.depart_city} → {flightDetails.arrive_city}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Stack>
                                                                        <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#95a5a6' }}>DETAILS</Typography>
                                                                    </Box>
                                                                ) : (
                                                                    /* 2. THE NORMAL FLIGHT CARD */
                                                                    <FlightCard
                                                                        this_flight={flightDetails}
                                                                        openStatusPicker={() => openStatusPicker(flightDetails)}
                                                                        showAccountDialog={props.showAccountDialog}
                                                                        setShowAccountDialog={props.setShowAccountDialog}
                                                                        openDeleteDialog={openDeleteDialog}
                                                                    />
                                                                )}

                                                                {/* 3. LAYOVER DIVIDER (Only show if this flight AND next flight aren't cancelled) */}
                                                                {!isCancelled && nextFlight && nextFlight.status !== "Cancelled" && layoverTime !== null && (
                                                                    <>
                                                                        <Divider sx={{ my: 2 }} />
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center', fontWeight: 700, opacity: 0.8 }}>
                                                                            Layover: {layoverHours > 0 && `${layoverHours} h`} {layoverMinutes > 0 && `${layoverMinutes} m`}
                                                                        </Typography>
                                                                        <Divider sx={{ my: 2 }} />
                                                                    </>
                                                                )}
                                                            </Grid>
                                                        );
                                                    })}
                                            </CardContent>
                                        </Card>
                                    );
                                })
                        )
                    }
                </div>

            </div>



            <Dialog
                open={isStatusPickerOpen}
                onClose={() => setStatusPickerOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#151269', pb: 1 }}>
                    Update Flight Details
                </DialogTitle>

                <DialogContent>
                    {/* --- STATUS OPTIONS LIST SELECTION --- */}
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                        SELECT OPERATIONAL STATUS
                    </Typography>

                    <List sx={{ pt: 0, mb: 2 }}>
                        {STATUS_OPTIONS.map((option) => (
                            <ListItemButton
                                key={option.code}
                                onClick={() => {
                                    // Updates status instantly and closes the dialog
                                    handleManualStatusUpdate(flightToUpdate, option.label);
                                    setStatusPickerOpen(false);
                                }}
                                sx={{
                                    borderRadius: '12px',
                                    mb: 1,
                                    transition: 'all 0.2s ease',
                                    bgcolor: flightToUpdate?.status === option.label ? `${option.color}08` : 'transparent',
                                    border: flightToUpdate?.status === option.label ? `2px solid ${option.color}` : '1px solid #f1f3f5',
                                    '&:hover': { bgcolor: '#f8fafc' }
                                }}
                            >
                                <ListItemIcon>
                                    <i className={option.icon} style={{ color: option.color, fontSize: '20px' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={option.label}
                                    primaryTypographyProps={{ fontWeight: 700, color: option.color }}
                                />
                                {flightToUpdate?.status === option.label && (
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: option.color }} />
                                )}
                            </ListItemButton>
                        ))}
                    </List>

                    <Divider sx={{ my: 2.5, borderColor: '#e2e8f0' }} />

                    {/* --- SCENARIO 3: MANUAL TIMESTAMP ADJUSTMENT PANEL --- */}
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                        MANUAL TIME OVERRIDES (DELAYED / CHANGED)
                    </Typography>

                    {flightToUpdate && (
                        <Stack spacing={2.5} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: '16px', bgcolor: '#f8fafc' }}>
                            <TextField
                                label="Departure Date/Time"
                                type="datetime-local"
                                fullWidth
                                value={flightToUpdate.depart_timestamp ? dayjs(flightToUpdate.depart_timestamp).format('YYYY-MM-DDTHH:mm') : ''}
                                onChange={(e) => {
                                    const newTime = e.target.value ? dayjs(e.target.value).format() : '';
                                    // Updates the timestamp dynamically while leaving status intact
                                    handleManualStatusUpdate({ ...flightToUpdate, depart_timestamp: newTime }, flightToUpdate.status);
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff' } }}
                            />

                            <TextField
                                label="Arrival Date/Time"
                                type="datetime-local"
                                fullWidth
                                value={flightToUpdate.arrive_timestamp ? dayjs(flightToUpdate.arrive_timestamp).format('YYYY-MM-DDTHH:mm') : ''}
                                onChange={(e) => {
                                    const newTime = e.target.value ? dayjs(e.target.value).format() : '';
                                    // Updates the timestamp dynamically while leaving status intact
                                    handleManualStatusUpdate({ ...flightToUpdate, arrive_timestamp: newTime }, flightToUpdate.status);
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff' } }}
                            />
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>


            <Dialog
                open={isCancelledDetailOpen}
                onClose={() => setCancelledDetailOpen(false)}
                PaperProps={{ sx: { borderRadius: '20px', width: '100%', maxWidth: '400px' } }}
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    {/* Header Icon */}
                    <Box sx={{
                        display: 'inline-flex',
                        bgcolor: 'rgba(231, 76, 60, 0.1)',
                        p: 2,
                        borderRadius: '50%',
                        mb: 2
                    }}>
                        <i className="icon-no" style={{ color: '#e74c3c', fontSize: '32px' }} />
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#151269' }}>
                        Cancelled Flight Details
                    </Typography>

                    <Box sx={{
                        my: 3,
                        p: 2,
                        bgcolor: '#f8f9fa',
                        borderRadius: '12px',
                        textAlign: 'left',
                        border: '1px solid #f1f3f5'
                    }}>
                        <Stack spacing={1.5}>
                            <Box>
                                <Typography sx={{ fontSize: '10px', fontWeight: 900, color: '#95a5a6', letterSpacing: 1 }}>FLIGHT</Typography>
                                <Typography sx={{ fontWeight: 700 }}>{selectedCancelledFlight?.airline_name} {selectedCancelledFlight?.flight_number}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', fontWeight: 900, color: '#95a5a6', letterSpacing: 1 }}>ROUTE</Typography>
                                <Typography sx={{ fontWeight: 700 }}>{selectedCancelledFlight?.depart_airport_code} → {selectedCancelledFlight?.arrive_airport_code}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', fontWeight: 900, color: '#95a5a6', letterSpacing: 1 }}>ORIGINAL DEPARTURE</Typography>
                                <Typography sx={{ fontWeight: 700 }}>{dayjs(selectedCancelledFlight?.depart_timestamp).format('MMM D, YYYY [at] h:mm A')}</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Stack spacing={2}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                setCancelledDetailOpen(false); // Close details
                                openStatusPicker(selectedCancelledFlight); // Open the status selector
                            }}
                            sx={{
                                bgcolor: '#151269',
                                borderRadius: '12px',
                                py: 1.5,
                                fontWeight: 800,
                                textTransform: 'none'
                            }}
                        >
                            Change Status / Restore
                        </Button>

                        <Button
                            fullWidth
                            onClick={() => {
                                setCancelledDetailOpen(false);
                                openDeleteDialog(selectedCancelledFlight);
                            }}
                            sx={{
                                color: '#e74c3c',
                                fontWeight: 700,
                                textTransform: 'none'
                            }}
                        >
                            Delete from Itinerary
                        </Button>

                        <Button
                            onClick={() => setCancelledDetailOpen(false)}
                            sx={{ color: '#95a5a6', fontWeight: 700, textTransform: 'none' }}
                        >
                            Close
                        </Button>
                    </Stack>
                </Box>
            </Dialog>


            {/* Flight Deletion Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={closeDeleteDialog}
                maxWidth="sm"
                PaperProps={{sx: {borderRadius: "20px"}}}
            >
                <Box sx={{p: 4, textAlign: 'center'}}>
                    <Box sx={{display: 'inline-flex', bgcolor: 'aliceblue', p: 2, borderRadius: '50%', mb: 2}}>
                        <i className="icon-airplane" style={{color: '#1976d2', fontSize: '40px'}}/>
                    </Box>

                    <DialogTitle variant="h4" fontWeight="bold" sx={{p: 0}}>
                        Delete Flight
                    </DialogTitle>

                    <DialogContent sx={{p: 0}}>
                        <Typography variant="body1" sx={{mt: 2, color: 'text.secondary', lineHeight: 1.6}}>
                            Confirming the permanent deletion of <Box component="span" sx={{
                            fontWeight: 'bold',
                            color: 'primary.main'
                        }}>Flight {flightToDelete?.flight_number}</Box> from your itinerary.
                        </Typography>

                        {/* Conditional Warning based on Co-Travelers */}
                        {coTravelerCount > 0 ? (
                            <Box sx={{
                                mt: 2, p: 2, bgcolor: '#fffde7', border: '1px solid #fff59d', borderRadius: '10px'
                            }}>
                                <Typography variant="body2" sx={{color: '#827717', fontWeight: 'bold'}}>
                                    <i className="icon-users" style={{marginRight: '8px'}}/>
                                    Note: {coTravelerCount} other attendee{coTravelerCount > 1 ? 's are' : ' is'} also
                                    on this flight. Deleting your entry will not affect their itineraries.
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{mt: 2, color: 'text.secondary'}}>
                                You are currently the only one on this specific flight entry.
                                This will be completely removed from your travel plan.
                            </Typography>
                        )}

                        <Typography variant="body1" sx={{ mt: 3, fontWeight: 'bold' }}>
                            Are you sure you want to proceed?
                        </Typography>
                    </DialogContent>

                    <DialogActions sx={{ p: 0, mt: 4, justifyContent: 'center' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
                            <Button onClick={closeDeleteDialog} variant="outlined" sx={{ borderRadius: '10px', flexGrow: 1 }}>
                                Keep Flight
                            </Button>
                            <Button
                                onClick={() => handleDeleteFlight(flightToDelete.id)}
                                variant="contained"
                                sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#115293' }, borderRadius: '10px', flexGrow: 1 }}
                            >
                                Delete Flight
                            </Button>
                        </Stack>
                    </DialogActions>
                </Box>
            </Dialog>
        </>

    )

}

export default FlightsList;
